var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import get from 'lodash/get';
import { getTracker } from 'conversional-analytics';
import { urlHelpers } from 'conversional-api';
// @ts-ignore
import * as callToActionUtils from 'conversional-utils/callToActions';
// @ts-ignore
import * as Events from 'conversional-journey/events';
import { CONVERSIONAL_LAUNCHER_CONFIG_DIV_ID } from './constants';
import { disableCookies, getConfig, getHostWidget, trackingEnabled } from './config';
import { startTracker, trackLaunchEvents, trackUrlParams } from './actions/tracking';
import { trackCTAViews } from './actions/callToActions';
import { initializeBundleErrorTracker } from './actions/errorTracker';
import { injectLauncherToHost } from './dom';
import { addLauncherStyles } from './styles';
import { applyDynamicStyling } from './styles/utils';
/**
 * Journeys with a `theme.booking_widget` config render the new booking widget
 * (book.conversion.al) instead of the legacy iframes, through the widget's own
 * loader. `data-widget="legacy"` on the snippet div keeps the legacy widget.
 * `data-display-mode` on the snippet div picks how it renders: `inline` (the
 * default, see findContainer), or `modal` / `drawer` / `fullscreen`, loaded
 * hidden and opened by a call to action (`.rt-stylecheck`) or `conversionalLauncher.show()`.
 * A journey with a launcher (`callToAction.loader`, or the legacy React
 * `complexLoader` buttons) gets it as a lightweight DOM launcher that opens a
 * drawer, as its legacy overlay did; its inline widget then renders only into a
 * container the page has (the journey's `elements` targets included).
 * `theme.booking_widget.placements` replaces that plan (and the snippet's mode):
 * every named inline whose container is on the page and every overlay, each its
 * own instance; the widget reads the named placement's filters.
 */
export var BOOKING_WIDGET_CONTAINER_ID = 'conversional-booking-widget';
// The legacy widget's inline container, already on existing embeds and the journey preview
var LEGACY_INLINE_CONTAINER_ID = 'conversional-inline-container';
var WIDGET_HOSTS = {
    production: 'https://book.conversion.al',
    testing: 'https://book-testing.conversion.al',
};
var DISPLAY_MODES = ['inline', 'modal', 'drawer', 'fullscreen'];
var active = null;
var placements = [];
// The legacy inline widget's containers (`elements` with a target), so existing page embeds keep working
var inlineTargets = [];
var loaderPromise = null;
var ctaListenerAdded = false;
// The call to action that opened the widget, reported with the booking like the legacy widget's lastCta
var lastCta = null;
var journeyId = function () { var _a; return ((_a = getConfig()) === null || _a === void 0 ? void 0 : _a.id) || (active === null || active === void 0 ? void 0 : active.journeyId) || '(not set)'; };
/** The widget's events go through the same tracker, config and host tools as the legacy iframes' (hostMessageHandler TRACK_EVENT) */
var relayEvent = function (event) {
    getTracker().event(__assign(__assign({}, event), { journeyId: journeyId() }));
    if (lastCta && event.category === 'Appointment' && event.action === 'created') {
        getTracker().event(__assign(__assign({}, Events.CONVERTINGCTA), { action: 'callToAction', label: lastCta, journeyId: journeyId() }));
    }
};
/** What createIframes does for the legacy widget before its iframes: error tracking, the tracker, the launch and CTA events */
var startTracking = function (journeyConfig) {
    var _a, _b;
    // initializeLauncher stores it for the legacy widget; getConfig(), the tracker's switches and the journey id read it there
    window.conversionalLauncher.journeyConfig = journeyConfig;
    var configuration = journeyConfig.configuration;
    initializeBundleErrorTracker(configuration);
    var tracker = startTracker(__assign(__assign({}, configuration), { noCookies: disableCookies(), enabled: trackingEnabled() }));
    // The host page's own tracking block (window.conversionalWidget.config.tracking) wins, as the legacy path's deep merge lets it
    var hostTracking = (_b = (_a = getHostWidget()) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.tracking;
    if (hostTracking)
        tracker === null || tracker === void 0 ? void 0 : tracker.updateConfig(hostTracking);
    trackLaunchEvents();
    trackUrlParams(urlHelpers.parseQueryString());
    trackCTAViews();
};
var widgetHost = function () { return WIDGET_HOSTS[process.env.REACT_APP_JOURNEY_ENV]; };
export var isBookingWidgetActive = function () { return active !== null; };
export var shouldRenderBookingWidget = function (configuration) {
    var _a;
    if (!((_a = configuration === null || configuration === void 0 ? void 0 : configuration.theme) === null || _a === void 0 ? void 0 : _a.booking_widget))
        return false;
    var configElement = document.getElementById(CONVERSIONAL_LAUNCHER_CONFIG_DIV_ID);
    if ((configElement === null || configElement === void 0 ? void 0 : configElement.getAttribute('data-widget')) === 'legacy')
        return false;
    return Boolean(widgetHost());
};
var loadLoader = function () {
    var existing = window.Conversional;
    if (existing === null || existing === void 0 ? void 0 : existing.showWidget)
        return Promise.resolve(existing);
    if (!loaderPromise) {
        loaderPromise = new Promise(function (resolve, reject) {
            var script = document.createElement('script');
            script.src = "".concat(widgetHost(), "/loader.iife.js");
            script.async = true;
            script.onload = function () { return resolve(window.Conversional); };
            script.onerror = function () {
                loaderPromise = null;
                script.remove();
                reject(new Error('[conversional] booking widget loader failed to load'));
            };
            document.head.appendChild(script);
        });
    }
    return loaderPromise;
};
var getContext = function () { var _a; return ((_a = window.conversionalLauncher) === null || _a === void 0 ? void 0 : _a.context) || {}; };
var snippetMode = function () {
    var _a;
    var mode = (_a = document.getElementById(CONVERSIONAL_LAUNCHER_CONFIG_DIV_ID)) === null || _a === void 0 ? void 0 : _a.getAttribute('data-display-mode');
    return mode && DISPLAY_MODES.includes(mode) ? mode : null;
};
// shortcut: a target that appears after start is not picked up (legacy retries for 10 s); add a retry when a page needs it
var findContainer = function () {
    return document.getElementById(BOOKING_WIDGET_CONTAINER_ID) ||
        inlineTargets.map(function (id) { return document.getElementById(id); }).find(Boolean) ||
        document.getElementById(LEGACY_INLINE_CONTAINER_ID);
};
// A page without a container gets one right after the snippet
var ensureContainer = function () {
    var existing = findContainer();
    if (existing)
        return existing;
    var container = document.createElement('div');
    container.id = BOOKING_WIDGET_CONTAINER_ID;
    var snippet = document.getElementById(CONVERSIONAL_LAUNCHER_CONFIG_DIV_ID);
    if (snippet)
        snippet.after(container);
    else
        document.body.appendChild(container);
    return container;
};
/**
 * Visibility lives on the container, driven by its `data-hidden` attribute.
 * Hidden keeps the real width (unlike display: none) so the calendar doesn't
 * lay out again on reveal; inert keeps it away from keyboard and screen readers.
 */
var applyVisibility = function (container) {
    var hidden = container.hasAttribute('data-hidden');
    container.style.height = hidden ? '0' : '';
    container.style.overflow = hidden ? 'hidden' : '';
    container.style.visibility = hidden ? 'hidden' : '';
    container.toggleAttribute('inert', hidden);
};
var setHidden = function (hidden) {
    var container = findContainer();
    if (!container)
        return;
    container.toggleAttribute('data-hidden', hidden);
    applyVisibility(container);
};
var inlineTargetsOf = function (configuration) {
    var elements = configuration === null || configuration === void 0 ? void 0 : configuration.elements;
    return (Array.isArray(elements) ? elements : []).flatMap(function (e) { var _a; return [].concat(((_a = e === null || e === void 0 ? void 0 : e.Window) === null || _a === void 0 ? void 0 : _a.target) || []); });
};
// `<$$locale.translations.x.y$$>` in launcher texts: the journey API sends callToAction unresolved
var resolvePlaceholders = function (text, configuration) {
    return String(text !== null && text !== void 0 ? text : '').replace(/<\$\$([\w.]+)\$\$>/g, function (_, path) {
        var value = get(configuration, path);
        return typeof value === 'string' ? value : '';
    });
};
/** The journey's launcher, drawn as DOM by bundle.js: `callToAction.loader`, else the legacy React launcher's first button */
export var launcherOf = function (configuration) {
    var _a, _b, _c, _d, _e;
    var cta = configuration === null || configuration === void 0 ? void 0 : configuration.callToAction;
    if ((cta === null || cta === void 0 ? void 0 : cta.loader) && typeof cta.loader === 'object')
        return cta.loader;
    var content = (_b = (_a = cta === null || cta === void 0 ? void 0 : cta.complexLoader) === null || _a === void 0 ? void 0 : _a.content) === null || _b === void 0 ? void 0 : _b.Content;
    var _f = (content === null || content === void 0 ? void 0 : content.type) === 'ButtonsLauncherContainer' ? ((_c = content.props) === null || _c === void 0 ? void 0 : _c.buttons) || [] : [], button = _f[0], rest = _f.slice(1);
    if (!(button === null || button === void 0 ? void 0 : button.text))
        return null;
    if (rest.length)
        console.warn('[conversional] the booking widget launcher shows only the first complexLoader button');
    return {
        type: 'ButtonLauncher',
        text: resolvePlaceholders(button.text, configuration),
        icon: button.prefixIcon ? 'calendarFilled' : 'none',
        showArrow: !!button.suffixIcon,
        boxShadow: (_e = (_d = content.props) === null || _d === void 0 ? void 0 : _d.buttonStyles) === null || _e === void 0 ? void 0 : _e.boxShadow,
    };
};
/** theme.booking_widget.placements, read without a schema (the widget validates it): entries it cannot use are skipped */
var backdropOf = function (value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : undefined;
};
var configuredPlacements = function (key, configuration) {
    var _a, _b;
    var raw = (_b = (_a = configuration === null || configuration === void 0 ? void 0 : configuration.theme) === null || _a === void 0 ? void 0 : _a.booking_widget) === null || _b === void 0 ? void 0 : _b.placements;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw) || !Object.keys(raw).length)
        return null;
    return Object.entries(raw).flatMap(function (_a) {
        var _b;
        var name = _a[0], p = _a[1];
        if (!DISPLAY_MODES.includes(p === null || p === void 0 ? void 0 : p.display)) {
            console.warn("[conversional] booking widget placement ".concat(name, " has no valid display; skipped"));
            return [];
        }
        var base = { key: "".concat(key, "-").concat(name), name: name };
        if (p.display !== 'inline') {
            return [__assign(__assign({}, base), { displayMode: p.display, cta: typeof p.cta === 'string' ? p.cta : undefined, launcher: p.launcher === true, backdrop: backdropOf(p.backdrop) })];
        }
        var ids = [].concat((_b = p.target) !== null && _b !== void 0 ? _b : []);
        var container = ids.length ? ids.map(function (id) { return document.getElementById(id); }).find(Boolean) : findContainer();
        return container ? [__assign(__assign({}, base), { displayMode: 'inline', container: container })] : [];
    });
};
var planPlacements = function (key, launcher, configuration) {
    var _a, _b;
    // The journey's backdrop for every overlay; a placement's own keys win
    var backdrop = backdropOf((_b = (_a = configuration === null || configuration === void 0 ? void 0 : configuration.theme) === null || _a === void 0 ? void 0 : _a.booking_widget) === null || _b === void 0 ? void 0 : _b.backdrop);
    return planFor(key, launcher, configuration).map(function (p) {
        return p.displayMode === 'inline' || !(backdrop || p.backdrop) ? p : __assign(__assign({}, p), { backdrop: __assign(__assign({}, backdrop), p.backdrop) });
    });
};
/**
 * Configured placements win. Otherwise the snippet's data-display-mode; without one, a journey with a launcher opens
 * a drawer from it and renders inline only where the page has a container; any other journey renders inline, after
 * the snippet if need be.
 */
var planFor = function (key, launcher, configuration) {
    var configured = configuredPlacements(key, configuration);
    if (configured)
        return configured;
    var mode = snippetMode();
    if (mode && mode !== 'inline')
        return [{ key: key, displayMode: mode }];
    if (!launcher || mode === 'inline')
        return [{ key: key, displayMode: 'inline', container: ensureContainer() }];
    var container = findContainer();
    return container
        ? [{ key: key, displayMode: 'inline', container: container }, { key: "".concat(key, "-overlay"), displayMode: 'drawer' }]
        : [{ key: key, displayMode: 'drawer' }];
};
var overlays = function () { return placements.filter(function (p) { return p.displayMode !== 'inline'; }); };
/** What the launcher, show() and an unmatched call to action open */
var overlay = function () { var _a; return (_a = overlays().find(function (p) { return p.launcher; })) !== null && _a !== void 0 ? _a : overlays()[0]; };
var matches = function (target, selector) {
    var _a;
    try {
        return !!((_a = target === null || target === void 0 ? void 0 : target.closest) === null || _a === void 0 ? void 0 : _a.call(target, selector));
    }
    catch (_b) {
        console.warn("[conversional] booking widget placement cta is not a valid selector: ".concat(selector));
        return false;
    }
};
var overlayFor = function (target) { var _a; return (_a = overlays().find(function (p) { return p.cta && matches(target, p.cta); })) !== null && _a !== void 0 ? _a : overlay(); };
var widgetOptions = function (placement) {
    var context = getContext();
    return __assign(__assign(__assign(__assign(__assign(__assign(__assign({}, active), context), { 
        // Always sent, so a context without answers clears earlier ones
        answers: context.answers || {}, widgetId: placement.key }), (placement.name ? { placement: placement.name } : {})), { displayMode: placement.displayMode }), (placement.backdrop ? { backdrop: placement.backdrop } : {})), (placement.container ? { inlineTargetSelector: "#".concat(placement.container.id) } : {}));
};
// Overlays load hidden and open on a call to action; inline shows right away
var mount = function (api) {
    return placements.forEach(function (p) { return (p.displayMode === 'inline' ? api.showWidget(widgetOptions(p)) : api.preload(widgetOptions(p))); });
};
var openFromLauncher = function () {
    lastCta = 'Launcher';
    getTracker().event(__assign(__assign({}, Events.LAUNCHER_CLICK), { journeyId: journeyId() }));
    showBookingWidget();
};
var PREFILL_KEYS = ['branchId', 'serviceTypeId', 'branchServiceId'];
// The widget resets the user's selection on every prefill it receives, so a
// call that doesn't change the location must not resend it
var withoutPrefill = function (options) {
    var rest = __assign({}, options);
    PREFILL_KEYS.forEach(function (key) { return delete rest[key]; });
    return rest;
};
export var startBookingWidget = function (settings, journeyConfig) {
    active = { journeyId: settings.journey, apiKey: settings.key };
    if (journeyConfig)
        startTracking(journeyConfig);
    var configuration = journeyConfig === null || journeyConfig === void 0 ? void 0 : journeyConfig.configuration;
    inlineTargets = inlineTargetsOf(configuration);
    var launcher = launcherOf(configuration);
    placements = planPlacements(settings.journey, !!launcher, configuration);
    placements.forEach(function (p) { return p.container && applyVisibility(p.container); });
    if (configuration && launcher && overlay()) {
        addLauncherStyles();
        applyDynamicStyling(configuration);
        injectLauncherToHost(configuration, launcher, openFromLauncher);
    }
    if (!ctaListenerAdded) {
        ctaListenerAdded = true;
        callToActionUtils.onCTAClick(function (label, target) {
            var _a;
            if (!isBookingWidgetActive())
                return;
            lastCta = label;
            getTracker().event(__assign(__assign({}, Events.CALLTOACTION_CLICK), { label: label, journeyId: journeyId() }));
            var opened = overlayFor(target);
            openOverlay(opened);
            if (!opened)
                (_a = findContainer()) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        callToActionUtils.createStyleTagForCTAs();
    }
    return loadLoader().then(function (api) {
        var _a;
        if (!active)
            return;
        (_a = api.onEvent) === null || _a === void 0 ? void 0 : _a.call(api, relayEvent);
        mount(api);
        window.conversionalClient = window.conversionalClient || {
            bookingWidget: true,
            show: showBookingWidget,
            hide: hideBookingWidget,
            setContext: setBookingWidgetContext,
        };
    });
};
/** Host API: reveals the widget. Works before the widget has loaded. */
export var showBookingWidget = function () { return openOverlay(overlay()); };
// Only the key (and the placement, should it mount now): no prefill, so the visitor's selection in a loaded overlay stays
var openOverlay = function (target) {
    if (!target)
        return setHidden(false);
    var named = __assign(__assign({}, (target.name ? { placement: target.name } : {})), (target.backdrop ? { backdrop: target.backdrop } : {}));
    if (active)
        loadLoader().then(function (api) { return active && api.showWidget(__assign(__assign(__assign(__assign({}, active), { widgetId: target.key }), named), { displayMode: target.displayMode })); });
};
/** Host API: hides the widget (its overlays when there are any); it stays loaded and keeps what the user entered */
export var hideBookingWidget = function () {
    if (!overlays().length)
        return setHidden(true);
    overlays().forEach(function (p) { var _a, _b; return (_b = (_a = window.Conversional) === null || _a === void 0 ? void 0 : _a.hideWidget) === null || _b === void 0 ? void 0 : _b.call(_a, p.key); });
};
/**
 * Host API: which branch / service type the widget books. Callable before the
 * bundle loads by setting `window.conversionalLauncher.context` directly.
 */
export var setBookingWidgetContext = function (context) {
    if (context === void 0) { context = {}; }
    var previous = window.conversionalLauncher.context || {};
    var locationChanged = PREFILL_KEYS.some(function (key) { return previous[key] !== context[key]; });
    window.conversionalLauncher.context = __assign({}, context);
    if (!active)
        return Promise.resolve();
    return loadLoader().then(function (api) {
        if (!active)
            return;
        var hasLocation = PREFILL_KEYS.some(function (key) { return context[key]; });
        // An empty prefill is never sent to a live widget, so clearing the location remounts it
        if (locationChanged && !hasLocation) {
            placements.forEach(function (p) { return api.resetWidget(p.key); });
            mount(api);
            return;
        }
        // preload() on a mounted widget only updates prefill and answers, keeping its visibility
        placements.forEach(function (p) { return api.preload(locationChanged ? widgetOptions(p) : withoutPrefill(widgetOptions(p))); });
    });
};
// The DOM launcher goes with removeFromDOM, which bootstrap calls beside this
export var destroyBookingWidget = function () {
    var _a;
    placements.forEach(function (p) { var _a, _b; return (_b = (_a = window.Conversional) === null || _a === void 0 ? void 0 : _a.resetWidget) === null || _b === void 0 ? void 0 : _b.call(_a, p.key); });
    if ((_a = window.conversionalClient) === null || _a === void 0 ? void 0 : _a.bookingWidget)
        delete window.conversionalClient;
    active = null;
    placements = [];
    inlineTargets = [];
};
